import React, { useState, useEffect, useCallback, useRef } from 'react';
// Using filled icons from public/icons
import { apiService } from '../../services/api';
import { ContactRead, ContactCreate, ContactUpdate, UserHorseNoteRead, UserHorseNoteCreate, NoteType } from '../../types/api';

interface HorseContactInfoProps {
  horseId: string;
  inCollection: boolean;
  isEditing?: boolean;
  initialContactNotes?: UserHorseNoteRead[];
}

export const HorseContactInfo: React.FC<HorseContactInfoProps> = ({ horseId, inCollection, isEditing = false, initialContactNotes }) => {
  const [contact, setContact] = useState<ContactRead | null>(null);
  const [contacts, setContacts] = useState<ContactRead[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [contactForm, setContactForm] = useState<{ name: string; address: string; phone: string; email: string }>({
    name: '',
    address: '',
    phone: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactNote, setContactNote] = useState<UserHorseNoteRead | null>(null);

  const cachedNotesRef = useRef<UserHorseNoteRead[] | undefined>(initialContactNotes);

  const loadContactInfo = useCallback(async () => {
    if (!apiService.isAuthenticated()) {
      setContact(null);
      setContacts([]);
      setSelectedId('');
      setContactForm({ name: '', address: '', phone: '', email: '' });
      setContactNote(null);
      return;
    }
    try {
      setLoading(true);
      
      let allContacts: ContactRead[] = [];
      let notes: UserHorseNoteRead[] = [];
      
      try {
        allContacts = await apiService.listContacts();
        allContacts = allContacts.filter(c => c.is_active !== false);
      } catch (contactError) {
        console.warn('Failed to load contacts list:', contactError);
      }
      
      if (cachedNotesRef.current !== undefined) {
        notes = cachedNotesRef.current;
        cachedNotesRef.current = undefined;
      } else {
        try {
          notes = await apiService.getHorseNotes(horseId, 'contact');
        } catch (notesError) {
          console.warn('Failed to load horse notes:', notesError);
        }
      }

      setContacts(allContacts);

      const foundNote = notes.find((note) => note.note_type === 'contact' && note.is_active);
      setContactNote(foundNote || null);

      let resolved: ContactRead | null = null;
      if (foundNote?.contact) {
        resolved = foundNote.contact;
      } else if (foundNote?.contact_id) {
        resolved = allContacts.find((c) => c.id === foundNote.contact_id) || null;
      }

      setContact(resolved);
      const initialId = resolved?.id || '';
      setSelectedId(initialId);
      setContactForm({
        name: resolved?.name || '',
        address: resolved?.address || '',
        phone: resolved?.phone || '',
        email: resolved?.email || '',
      });
    } catch (error) {
      console.warn('Failed to load contact info:', error);
      setContact(null);
      setContacts([]);
      setSelectedId('');
      setContactForm({ name: '', address: '', phone: '', email: '' });
      setContactNote(null);
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  useEffect(() => {
    loadContactInfo();
  }, [loadContactInfo]);

  const upsertContactNote = useCallback(
    async (contactId: string, contactName: string) => {
      try {
        const noteData: UserHorseNoteCreate = {
          title: contactName ? `Contact: ${contactName}` : 'Contact',
          note_type: 'contact' as NoteType,
          content: 'Seller contact info',
          contact_id: contactId,
        };
        let updated: UserHorseNoteRead;
        if (contactNote?.id) {
          updated = await apiService.updateHorseNote(horseId, contactNote.id, noteData);
        } else {
          updated = await apiService.createHorseNote(horseId, noteData);
        }
        setContactNote(updated);
      } catch (err) {
        console.warn('Failed to link contact to horse', err);
      }
    },
    [contactNote, horseId]
  );

  const persistContact = useCallback(async () => {
    if (saving) return;
    const name = contactForm.name.trim();
    const normalize = (val: string) => {
      const t = val.trim();
      return t === '' ? null : t;
    };
    const address = normalize(contactForm.address);
    const phone = normalize(contactForm.phone);
    const email = normalize(contactForm.email);
    if (!name) return;

    setSaving(true);
    try {
      let saved: ContactRead;
      if (selectedId && selectedId !== 'new') {
        const payload: ContactUpdate = {
          name,
          address: (address === null ? null : address) as any,
          phone: (phone === null ? null : phone) as any,
          email: (email === null ? null : email) as any,
        };
        saved = await apiService.updateContact(selectedId, payload);
        setContacts((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
      } else {
        const payload: ContactCreate = {
          name,
          address: (address === null ? null : address) as any,
          phone: (phone === null ? null : phone) as any,
          email: (email === null ? null : email) as any,
        };
        saved = await apiService.createContact(payload);
        setContacts((prev) => [saved, ...prev]);
        setSelectedId(saved.id);
      }
      setContact(saved);
      await upsertContactNote(saved.id, saved.name || 'Contact');
      // Reload from API to ensure cleared fields reflect server truth
      await loadContactInfo();
    } catch (err) {
      console.warn('Failed to save contact', err);
    } finally {
      setSaving(false);
    }
  }, [contactForm, selectedId, saving, upsertContactNote, loadContactInfo]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    if (id === 'new') {
      setContact(null);
      setContactForm({ name: '', address: '', phone: '', email: '' });
      return;
    }
    const found = contacts.find((c) => c.id === id);
    setContact(found || null);
    setContactForm({
      name: found?.name || '',
      address: found?.address || '',
      phone: found?.phone || '',
      email: found?.email || '',
    });
    if (found) {
      upsertContactNote(found.id, found.name || 'Contact');
    }
  };

  const handleInputChange = (field: 'name' | 'address' | 'phone' | 'email', value: string) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  if (!apiService.isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="text-sm text-[#233454]">
        Loading contact...
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {isEditing ? (
        <>
          {/* Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Contact:</span>
            <select
              value={selectedId}
              onChange={(e) => handleSelect(e.target.value)}
              className="bg-[#ebf6e4] rounded-[5px] h-[36px] px-3 py-1.5 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 flex-1 min-w-0 max-w-full"
            >
              <option value="">Select contact</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value="new">+ Add new contact</option>
            </select>
          </div>
          {/* Name (only when adding new contact) */}
          {selectedId === 'new' && (
            <div className="flex items-center gap-2">
              <span className="w-[56px] shrink-0 flex justify-end">
                <img src="/icons/filled_user_icon.svg" alt="" className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                value={contactForm.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                onBlur={persistContact}
                className="bg-[#ebf6e4] rounded-[5px] h-[32px] px-3 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 flex-1 min-w-0"
                placeholder="Add name"
              />
            </div>
          )}
          {/* Location */}
          <div className="flex items-center gap-2">
            <span className="w-[56px] shrink-0 flex justify-end">
              <img src="/icons/filled_maps_and_flags_icon.svg" alt="" className="w-3.5 h-3.5" />
            </span>
            <input
              type="text"
              value={contactForm.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              onBlur={persistContact}
              className="bg-[#ebf6e4] rounded-[5px] h-[32px] px-3 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 flex-1 min-w-0"
              placeholder="Add location"
            />
          </div>
          {/* Phone */}
          <div className="flex items-center gap-2">
            <span className="w-[56px] shrink-0 flex justify-end">
              <img src="/icons/filled_call_icon.svg" alt="" className="w-3.5 h-3.5" />
            </span>
            <input
              type="text"
              value={contactForm.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={persistContact}
              className="bg-[#ebf6e4] rounded-[5px] h-[32px] px-3 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 flex-1 min-w-0"
              placeholder="Add phone"
            />
          </div>
          {/* Email */}
          <div className="flex items-center gap-2">
            <span className="w-[56px] shrink-0 flex justify-end">
              <img src="/icons/filled_email_icon.svg" alt="" className="w-3.5 h-3.5" />
            </span>
            <input
              type="email"
              value={contactForm.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={persistContact}
              className="bg-[#ebf6e4] rounded-[5px] h-[32px] px-3 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 flex-1 min-w-0"
              placeholder="Add email"
            />
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Contact:</span>
            <span className="text-sm text-[#233454]">{contact?.name}</span>
          </div>
          {contact?.address && (
            <div className="flex items-center gap-2">
              <span className="w-[56px] shrink-0 flex justify-end">
                <img src="/icons/filled_maps_and_flags_icon.svg" alt="" className="w-3.5 h-3.5" />
              </span>
              <p className="text-sm text-[#233454]">{contact.address}</p>
            </div>
          )}
          {contact?.phone && (
            <div className="flex items-center gap-2">
              <span className="w-[56px] shrink-0 flex justify-end">
                <img src="/icons/filled_call_icon.svg" alt="" className="w-3.5 h-3.5" />
              </span>
              <p className="text-sm text-[#233454]">{contact.phone}</p>
            </div>
          )}
          {contact?.email && (
            <div className="flex items-center gap-2">
              <span className="w-[56px] shrink-0 flex justify-end">
                <img src="/icons/filled_email_icon.svg" alt="" className="w-3.5 h-3.5" />
              </span>
              <p className="text-sm text-[#233454]">{contact.email}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

